import React from 'react'
import { Button, Typography } from '@grammarly/badger-ui'

import { navHelper } from '../../../../../lib/services/navigation'
import { IconWarning } from '../../../../ui-lib/icons/warning'
import { IconClose } from '../../../../ui-lib/icons/close'
import { SpinnerIcon } from '../../../../ui-lib/icons/spinner'
import { ButtonCta } from '../../../../ui-lib/button'
import { FixedSpacer } from '../../../../ui-lib/spacer'

import { UploadButton } from '../upload-button'

import { TextCheckStatus } from '../types'
import styles from './overlay.module.scss'

const { C2, C3 } = Typography

interface TextCheckerOverlayProps {
  status: TextCheckStatus
  setStatus: (status: TextCheckStatus) => void
  setText: (text: string) => void
  onClose: () => void
  scanForPlagiarism?: boolean
}

const MainText: React.FC<{ children?: React.ReactNode }> = (props) => (
  <C2 as="div" bold>
    {props.children}
  </C2>
)

const SubText: React.FC<{ children?: React.ReactNode }> = (props) => (
  <C3 as="div" className={styles.subText}>
    {props.children}
  </C3>
)

const NoAlertsCTA: React.FC = () => {
  //TODO: add tracking
  return <ButtonCta onClick={() => navHelper.navigateFunnelPath('/plans')} text="Learn More" />
}

const overlayLoadingText = {
  plagiarism: {
    mainText: <MainText>Scan for plagiarism</MainText>,
    subText: <SubText>Grammarly is looking for plagiarism and writing mistakes.</SubText>,
  },
  default: {
    subText: <SubText>Grammarly is proofreading your text</SubText>,
  },
}

export const TextCheckerOverlay = ({
  status,
  onClose,
  setStatus,
  setText,
  scanForPlagiarism,
}: TextCheckerOverlayProps) => {
  const showCloseButton = status !== 'LOADING_TEXT_CHECK' && status !== 'LOADING_FILE'

  return (
    <div className={styles.container}>
      {showCloseButton && (
        <Button className={styles.closeButton} onClick={onClose} padding="none" aria-label="Close">
          <IconClose aria-hidden="true" />
        </Button>
      )}
      <div className={styles.contentContainer}>
        {status === 'LOADING_TEXT_CHECK' && (
          <>
            <SpinnerIcon />
            {scanForPlagiarism ? overlayLoadingText.plagiarism.mainText : null}
            {scanForPlagiarism ? overlayLoadingText.plagiarism.subText : overlayLoadingText.default.subText}
          </>
        )}
        {status === 'LOADING_FILE' && <SpinnerIcon />}
        {status === 'NO_ALERTS_FOUND' && (
          <>
            <MainText>Nice Work!</MainText>
            <FixedSpacer multiplier={1} />
            <SubText>
              Grammarly found no errors in your text. Learn more about how Grammarly can help improve your writing.
            </SubText>
            <FixedSpacer multiplier={2} />
            <NoAlertsCTA />
          </>
        )}
        {status === 'UNKNOWN_ERROR' && (
          <>
            <MainText>oops!</MainText>
            <FixedSpacer multiplier={1} />
            <SubText>
              We are having trouble checking your text right now. Learn more about how Grammarly can help improve your
              writing.
            </SubText>
            <FixedSpacer multiplier={2} />
            <NoAlertsCTA />
          </>
        )}
        {(status === 'TOO_MUCH_TEXT' || status === 'FILE_ERROR_TOO_BIG') && (
          <>
            <IconWarning />
            <MainText>Too much text!</MainText>
            <SubText>Try checking less than 20 pages at a time.</SubText>
            <UploadButton setStatus={setStatus} setText={setText} />
          </>
        )}
        {status === 'FILE_ERROR_INVALID_EXT' && (
          <>
            <IconWarning />
            <MainText>This file type is unsupported.</MainText>
            <SubText>Supported types: .doc, .docx, .odt, .txt and .rtf</SubText>
            <UploadButton setStatus={setStatus} setText={setText} />
          </>
        )}
      </div>
    </div>
  )
}
